/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import I18n from 'I18n';
import { createContext, useEffect, useMemo, useContext, useCallback, useState } from 'react';
import { useFullPlaylist, useRemoveFromPlaylist, useUpdatePlaylist } from '../../savedItems/savedItemsQueries';
import { swapArrayItems, useUpdatePlaylistItemPosition } from '../playlistItemsQueries';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import produce from 'immer';
import { jsx as _jsx } from "react/jsx-runtime";
const noop = () => {};
const noopPromise = () => Promise.resolve();
const useLocalDraft = value => {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (value && draft === null) {
      setDraft(value);
    }
  }, [draft, value]);
  return [draft || [], setDraft];
};
const PlaylistItemManagerContext = /*#__PURE__*/createContext({
  playlistId: 0,
  playlistItems: [],
  addPlaylistItem: noop,
  removePlaylistItem: noop,
  movePlaylistItem: noop,
  saveNewPlaylistItems: noopPromise
});
export const usePlaylistItemManager = () => {
  const state = useContext(PlaylistItemManagerContext);
  return Object.assign({}, state, {
    numberOfSelectedItems: state.playlistItems.length
  });
};
export const PlaylistItemManager = ({
  playlistId,
  render
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const [playlistDraft, setPlaylistDraft] = useLocalDraft((playlist === null || playlist === void 0 ? void 0 : playlist.items) || null);
  const [removeFromPlaylist] = useRemoveFromPlaylist({
    showNotification: false
  });
  const [updatePlaylist] = useUpdatePlaylist(playlistId, {
    onError() {
      setPlaylistDraft((playlist === null || playlist === void 0 ? void 0 : playlist.items) || null);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.savedItems.playlistDetails.playlistItemCouldNotBeSaved')
      });
    }
  });
  const updatePlaylistItemPosition = useUpdatePlaylistItemPosition(playlistId, {
    onError() {
      setPlaylistDraft((playlist === null || playlist === void 0 ? void 0 : playlist.items) || null);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.savedItems.playlistDetails.playlistItemCouldNotBeMoved')
      });
    }
  });
  const playlistItemsToRender = useMemo(() => {
    if (!playlist || !playlistDraft) return [];
    return playlistDraft.map(playlistItem => Object.assign({}, playlistItem, {
      hasBeenRemoved: !playlist.items.some(item => item.itemId === playlistItem.itemId)
    }));
  }, [playlist, playlistDraft]);
  const addPlaylistItem = useCallback(playlistItem => {
    /**
     * Rather than add the item using the dedicated endpoint, we want
     * to add it preserving the position in the playlist that it had.
     * Otherwise if the user removes an item and then adds it back, it
     * will be at the end of the playlist which is unexpected.
     */
    return updatePlaylist({
      items: playlistItemsToRender.filter(item => {
        if (item.itemId === playlistItem.itemId && item.type === playlistItem.type && item.language === playlistItem.language) {
          return true;
        }
        return !item.hasBeenRemoved;
      })
    });
  }, [playlistItemsToRender, updatePlaylist]);
  const movePlaylistItem = useCallback((fromIndex, toIndex) => {
    updatePlaylistItemPosition(fromIndex, toIndex);
    setPlaylistDraft(oldDraft => produce(oldDraft, newDraft => {
      swapArrayItems(newDraft, fromIndex, toIndex);
    }));
  }, [setPlaylistDraft, updatePlaylistItemPosition]);
  const removePlaylistItem = useCallback(playlistItem => {
    return removeFromPlaylist({
      playlistId,
      itemId: playlistItem.itemId,
      type: playlistItem.type,
      language: playlistItem.language
    });
  }, [playlistId, removeFromPlaylist]);
  const saveNewPlaylistItems = useCallback(items => {
    const newItems = items.filter(item => !item.hasBeenRemoved).map(item => {
      return Object.assign({}, item, {
        type: item.type === 'SHORT_FORM_CONTENT' ? 'SHORT_FORM_CONTENT' : item.type === 'TRACK' ? 'TRACK' : 'LESSON'
      });
    });
    return updatePlaylist({
      items: newItems
    }).then(() => {
      setPlaylistDraft(newItems);
    });
  }, [setPlaylistDraft, updatePlaylist]);
  const contextState = useMemo(() => ({
    playlistId,
    playlistItems: playlistItemsToRender,
    addPlaylistItem,
    removePlaylistItem,
    movePlaylistItem,
    saveNewPlaylistItems
  }), [addPlaylistItem, movePlaylistItem, playlistId, playlistItemsToRender, removePlaylistItem, saveNewPlaylistItems]);
  return /*#__PURE__*/_jsx(PlaylistItemManagerContext.Provider, {
    value: contextState,
    children: render(playlistItemsToRender)
  });
};